import {
  Fragment,
  useMemo,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { When } from "react-if";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { IEmailPerformanceTotals, IEmailTemplateStats } from "shared";

import { Badge } from "../../../common/Atoms/Badge";
import { Select } from "../../../common/Atoms/Select";
import { formatAmount } from "../../../common/lib/helpers";
import { templateGroupLabels } from "../Settings/EmailTemplates/constants";

import {
  clickRate,
  formatCount,
  formatPct,
  openRate,
  paidWithin7Rate,
  spamRate,
  templateLabel,
} from "./stats";

export type SortKey = `sent` | `open` | `click` | `spam` | `paid` | `days` | `collected`;

interface IColumn {
  key: SortKey;
  label: string;
  /** Lower is better, so the default sort direction is ascending */
  asc?: boolean;
  value: (t: IEmailTemplateStats) => number | null;
}

const COLUMNS: IColumn[] = [
  { key: `sent`, label: `Sent`, value: t => t.sent },
  { key: `open`, label: `Open rate`, value: openRate },
  { key: `click`, label: `Click rate`, value: clickRate },
  { key: `spam`, label: `Spam rate`, asc: true, value: spamRate },
  { key: `paid`, label: `Paid in 7 days`, value: paidWithin7Rate },
  { key: `days`, label: `Median days to pay`, asc: true, value: t => t.medianDaysToPay },
  { key: `collected`, label: `Collected`, value: t => t.collectedHomeCents },
];

const ALL_TYPES = `all`;

function MetricCells({ t, homeCurrency }: { t: IEmailPerformanceTotals; homeCurrency: string }) {
  const open = openRate(t);

  return (
    <>
      <td className={ `px-3 py-3.5 text-right tabular-nums text-gray-700` }>{ formatCount(t.sent) }</td>
      <td className={ `px-3 py-3.5 text-right` }>
        <div className={ `flex items-center justify-end gap-2` }>
          <div className={ `h-1.5 w-16 overflow-hidden rounded-full bg-gray-100` }>
            <div className={ `h-full bg-paidniceGreen-500` }
              style={ { width: `${open ?? 0}%` } } />
          </div>
          <span className={ `w-11 tabular-nums` }
            title={ `${t.opened} of ${t.delivered} delivered` }>{ formatPct(open) }</span>
        </div>
      </td>
      <td className={ `px-3 py-3.5 text-right tabular-nums` }
        title={ `${t.clicked} of ${t.delivered} delivered` }>{ formatPct(clickRate(t)) }</td>
      <td className={ `px-3 py-3.5 text-right tabular-nums ${t.spam > 0 ? `text-paidniceRed-600` : ``}` }
        title={ `${t.spam} of ${t.delivered} delivered` }>{ formatPct(spamRate(t)) }</td>
      <td className={ `px-3 py-3.5 text-right tabular-nums` }
        title={ `${t.paidWithin7} of ${t.invoiceEmails} invoice emails` }>{ formatPct(paidWithin7Rate(t)) }</td>
      <td className={ `px-3 py-3.5 text-right tabular-nums` }>{ t.medianDaysToPay === null ? `–` : t.medianDaysToPay.toFixed(1) }</td>
      <td className={ `py-3.5 pl-3 pr-5 text-right font-semibold tabular-nums` }>{ formatAmount(t.collectedHomeCents, homeCurrency) }</td>
    </>
  );
}

interface IProps {
  organisationId: string;
  templates: IEmailTemplateStats[];
  homeCurrency: string;
}

export function TemplateStatsTable({ organisationId, templates, homeCurrency }: IProps) {
  const [sort, setSort] = useState<SortKey>(`paid`);
  const [type, setType] = useState<string>(ALL_TYPES);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (key: string) => setExpanded(prev => {
    const next = new Set(prev);

    if (!next.delete(key)) {
      next.add(key);
    }

    return next;
  });

  const column = COLUMNS.find(c => c.key === sort) ?? COLUMNS[0];

  const typeOptions = useMemo(() => {
    const present = new Set(templates.map(t => t.templateType).filter((t): t is string => !!t));

    return [
      { label: `All types`, value: ALL_TYPES },
      ...[...present].map(value => ({ label: templateGroupLabels[value] ?? value, value })),
    ];
  }, [templates]);

  const rows = useMemo(() => {
    const filtered = templates.filter(t => type === ALL_TYPES || t.templateType === type);

    // Nulls (no data) always sink to the bottom whichever way we sort
    return [...filtered].sort((a, b) => {
      const av = column.value(a);
      const bv = column.value(b);

      if (av === null || bv === null) {
        return av === null ? (bv === null ? 0 : 1) : -1;
      }

      return column.asc ? av - bv : bv - av;
    });
  }, [templates, type, column]);

  const topKey = rows[0] && column.value(rows[0]) !== null ? rows[0].templateId ?? `none` : null;

  return (
    <div className={ `overflow-hidden rounded-lg border border-gray-200 bg-white` }>
      <div className={ `flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4` }>
        <h2 className={ `text-base font-semibold text-gray-900` }>{ `Templates` }</h2>
        <div className={ `w-[200px]` }>
          <Select
            options={ typeOptions }
            selected={ type }
            onChange={ ({ value }) => setType(String(value)) }
            size={ `sm` }
          />
        </div>
      </div>
      <div className={ `overflow-x-auto` }>
        <table className={ `w-full min-w-[960px] border-collapse text-sm` }>
          <thead>
            <tr className={ `bg-gray-50` }>
              <th className={ `px-5 py-2.5 text-left text-xs font-semibold text-gray-500` }>{ `Template` }</th>
              { COLUMNS.map(c => (
                <th key={ c.key }
                  className={ `whitespace-nowrap px-3 py-2.5 text-right text-xs font-semibold` }>
                  <button type={ `button` }
                    onClick={ () => setSort(c.key) }
                    className={ c.key === sort ? `text-paidniceGreen-700` : `text-gray-500 hover:text-gray-700` }>
                    { c.label }{ c.key === sort ? (c.asc ? ` ↑` : ` ↓`) : `` }
                  </button>
                </th>
              )) }
            </tr>
          </thead>
          <tbody>
            { rows.map(t => {
              const key = t.templateId ?? `none`;
              const variations = t.variations ?? [];
              const isExpanded = expanded.has(key);

              return (
                <Fragment key={ key }>
                  <tr className={ `border-t border-gray-100 hover:bg-gray-50` }>
                    <td className={ `px-5 py-3.5` }>
                      <div className={ `flex flex-col items-start gap-1.5` }>
                        <div className={ `flex items-center gap-2` }>
                          <When condition={ !!t.templateId && !!t.templateName }>
                            <Link to={ `/${organisationId}/settings/email-templates/${t.templateId}` }
                              className={ `font-semibold text-gray-900 hover:text-paidniceGreen-700` }>
                              { t.templateName }
                            </Link>
                          </When>
                          <When condition={ !t.templateId || !t.templateName }>
                            <span className={ `font-semibold text-gray-500` }>{ templateLabel(t) }</span>
                          </When>
                          <When condition={ key === topKey }>
                            <Badge color={ `green` }
                              size={ `xs` }
                              message={ `Top` } />
                          </When>
                        </div>
                        <div className={ `text-xs text-gray-500` }>
                          { t.templateType ? (templateGroupLabels[t.templateType] ?? t.templateType) : `Sent without a policy template` }
                          { t.subject ? ` · “${t.subject}”` : `` }
                        </div>
                        <When condition={ variations.length > 0 }>
                          <button type={ `button` }
                            onClick={ () => toggle(key) }
                            aria-expanded={ isExpanded }
                            className={ `flex items-center gap-0.5 text-xs font-medium text-paidniceGreen-700 hover:text-paidniceGreen-800` }>
                            { isExpanded
                              ? <ChevronDownIcon className={ `h-4 w-4` } />
                              : <ChevronRightIcon className={ `h-4 w-4` } /> }
                            { `${variations.length} variations` }
                          </button>
                        </When>
                      </div>
                    </td>
                    <MetricCells t={ t }
                      homeCurrency={ homeCurrency } />
                  </tr>
                  { isExpanded && variations.map(v => (
                    <tr key={ `${key}:${v.variationId}` }
                      className={ `bg-gray-50/60 text-gray-600` }>
                      <td className={ `py-3.5 pl-10 pr-5` }>
                        <div className={ `flex flex-col items-start gap-1` }>
                          <span className={ `font-medium text-gray-800` }>{ v.isOriginal ? `Original` : v.name }</span>
                          <When condition={ !!v.subject }>
                            <span className={ `text-xs text-gray-500` }>{ `“${v.subject}”` }</span>
                          </When>
                        </div>
                      </td>
                      <MetricCells t={ v }
                        homeCurrency={ homeCurrency } />
                    </tr>
                  )) }
                </Fragment>
              );
            }) }
          </tbody>
        </table>
        <When condition={ rows.length === 0 }>
          <div className={ `py-10 text-center text-sm text-gray-500` }>{ `No templates match this filter.` }</div>
        </When>
      </div>
    </div>
  );
}
